import { useEffect, useState, ReactElement, cloneElement } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@mui/material';

interface NccnInfoTooltipProps {
  children: ReactElement<{ onClick?: (event: React.MouseEvent) => void }>;
}

export const NccnInfoTooltip: React.FC<NccnInfoTooltipProps> = ({
  children,
}) => {
  const theme = useTheme();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Reset image loaded state when dialog opens or theme changes
  useEffect(() => {
    if (isDialogOpen) {
      setIsImageLoaded(false);
    }
  }, [isDialogOpen, theme.palette.mode]);

  const handleOpen = () => setIsDialogOpen(true);
  const handleClose = () => {
    setIsDialogOpen(false);
    setIsImageLoaded(false);
  };

  // Clone the child element and add onClick handler
  // If the child already has an onClick, call it first, then open the dialog
  const triggerElement = cloneElement(children, {
    onClick: (event: React.MouseEvent) => {
      children.props.onClick?.(event);
      handleOpen();
    },
  });

  return (
    <>
      {triggerElement}
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        data-testid='nccn-info-dialog'
        slotProps={{
          paper: {
            sx: {
              maxWidth: '850px',
            },
          },
        }}
      >
        <DialogTitle
          data-testid='nccn-info-dialog-title'
          sx={{
            textAlign: 'left',
            fontSize: {
              xs: theme.typography.h6.fontSize,
              sm: theme.typography.h5.fontSize,
            },
          }}
        >
          National Comprehensive Cancer Network® (NCCN®)
          <br />
          Risk Category Classification
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingTop: 2 }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                minHeight: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
              }}
            >
              {!isImageLoaded && (
                <CircularProgress
                  data-testid='nccn-info-image-loading'
                  size={40}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
              <img
                data-testid='nccn-info-image'
                src={
                  theme.palette.mode === 'dark'
                    ? '/content/nccn-risk-category-guidelines-dark.png'
                    : '/content/nccn-risk-category-guidelines-light.png'
                }
                alt='NCCN Risk Category Guidelines'
                onLoad={() => setIsImageLoaded(true)}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: isImageLoaded ? 'block' : 'none',
                }}
              />
            </Box>
            <Typography
              data-testid='nccn-info-attribution'
              variant='caption'
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 2,
              }}
            >
              Adapted with permission from the NCCN Clinical Practice Guidelines
              in Oncology (NCCN Guidelines®) for Prostate Cancer V2.2025 © 2025
              National Comprehensive Cancer Network, Inc. All rights reserved.
              The NCCN Guidelines® and illustrations herein may not be
              reproduced in any form for any purpose without the express written
              permission of NCCN. To view the most recent and complete version
              of the NCCN Guidelines, go online to NCCN.org. The NCCN Guidelines
              are a work in progress that may be refined as often as new
              significant data becomes available. NCCN makes no warranties of
              any kind whatsoever regarding their content, use or application
              and disclaims any responsibility for their application or use in
              any way.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
